import React, { useState } from "react";
import { format } from "date-fns";
import { Laptop, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { useGetDevicesQuery, useRevokeDeviceMutation } from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { getDeviceId } from "@/util/device";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserDevice } from "@/model/interface/TwoFactor";

export default function TrustedDevicesSection() {
  const { data: devices, isLoading } = useGetDevicesQuery();
  const [revokeDevice, revokeResult] = useRevokeDeviceMutation();
  const [deviceToRevoke, setDeviceToRevoke] = useState<UserDevice | null>(null);

  const currentDeviceId = getDeviceId();

  const onRevoke = async () => {
    if (!deviceToRevoke) {
      return;
    }
    try {
      await revokeDevice(deviceToRevoke.id).unwrap();
      toast.success("Device signed out");
      setDeviceToRevoke(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to sign out the device"));
    }
  };

  if (isLoading) {
    return <Skeleton className="h-48 w-full max-w-2xl" />;
  }

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle>Devices</CardTitle>
        <CardDescription>
          Devices signed in to your account. Trusted devices skip two-factor verification for 30 days.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!devices?.length && (
          <p className="text-sm text-muted-foreground">No active devices.</p>
        )}
        {!!devices?.length && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.map((device) => {
                const isCurrent = device.id === currentDeviceId;
                return (
                  <TableRow key={device.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {device.platform === "WEB" ? (
                          <Laptop className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <Smartphone className="h-4 w-4 text-muted-foreground" />
                        )}
                        <div className="flex items-center text-sm font-medium">
                          {device.name || "Unknown device"}
                          {isCurrent && (
                            <Badge variant="secondary" className="ml-2">
                              This device
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {device.lastUsedAt ? format(new Date(device.lastUsedAt), "MMM d, yyyy HH:mm") : "Never"}
                    </TableCell>
                    <TableCell>
                      {device.trusted ? (
                        <Badge variant="secondary">
                          Trusted until{" "}
                          {device.trustedUntil ? format(new Date(device.trustedUntil), "MMM d") : ""}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">Not trusted</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => setDeviceToRevoke(device)}
                      >
                        Sign out
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <AlertDialog open={!!deviceToRevoke} onOpenChange={(open) => !open && setDeviceToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sign out this device?</AlertDialogTitle>
            <AlertDialogDescription>
              {deviceToRevoke?.name || "This device"} will be signed out remotely and will need to
              verify with two-factor authentication on its next sign in.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onRevoke} disabled={revokeResult.isLoading}>
              {revokeResult.isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign out device
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
